// iPhone More page. Opens from the More tab and sits between the dock and the
// tab bar. It keeps everything the old More menu reached: the profile, the
// daily tools, community, the library, the full section directory, push
// notifications, the guided tour, and sign out. Coach accounts switch to
// Command with the top bar's Training and Command switch, not from here.
import { useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'wouter';
import {
  BarChart3, Bell, BellOff, BookMarked, BookOpen, CalendarDays, ChevronDown, ChevronRight, ClipboardList,
  Clock, LayoutGrid, LogOut, MessageSquare, PenLine, Shield, Sparkles, Trophy, UserCircle, Users,
} from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';

type IconType = ComponentType<{ className?: string; strokeWidth?: number; 'aria-hidden'?: boolean | 'true' }>;

export type PhoneMoreSection = {
  label: string;
  description?: string;
  icon?: IconType;
  items: Array<{ path: string; label: string; desc: string; icon: IconType }>;
};

type Props = {
  user: { id: number; name?: string | null; fullName?: string | null; email?: string | null; avatarUrl?: string | null } | null | undefined;
  location: string;
  hrefFor: (href: string) => string;
  sections: PhoneMoreSection[];
  inPerson: boolean;
  showMessages: boolean;
  unreadMessages: number;
  onClose: () => void;
  onTour: () => void;
  onSignOut: () => void;
  signingOut: boolean;
};

type Row = { href: string; label: string; icon: IconType; tone?: 'steel' | 'bronze' | 'dark'; badge?: number; note?: string };

function RowLink({ row, hrefFor, onClose }: { row: Row; hrefFor: (href: string) => string; onClose: () => void }) {
  const Icon = row.icon;
  return (
    <Link href={hrefFor(row.href)} onClick={onClose} className="iphone-more__row"
      aria-label={row.badge ? `${row.label}, ${row.badge} unread` : undefined}>
      <span className="iphone-more__ic" data-tone={row.tone ?? 'steel'}><Icon className="size-[18px]" strokeWidth={1.8} aria-hidden="true" /></span>
      <span className="iphone-more__text">{row.label}</span>
      {row.badge ? <span className="iphone-more__count" aria-hidden="true">{row.badge > 99 ? '99+' : row.badge}</span> : null}
      {row.note ? <em>{row.note}</em> : null}
    </Link>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  const id = `iphone-more-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  return (
    <div className="iphone-more__group">
      <p className="iphone-more__grp" id={id}>{title}</p>
      <nav className="iphone-more__list" aria-labelledby={id}>{children}</nav>
    </div>
  );
}

function PushRow() {
  const { isSupported, isSubscribed, isLoading, toggle } = usePushNotifications();
  if (!isSupported) return null;
  return (
    <button type="button" className="iphone-more__row" onClick={toggle} disabled={isLoading} aria-pressed={isSubscribed}>
      <span className="iphone-more__ic">{isSubscribed ? <Bell className="size-[18px]" aria-hidden="true" /> : <BellOff className="size-[18px]" aria-hidden="true" />}</span>
      <span className="iphone-more__text">Push notifications</span>
      <em>{isLoading ? 'Saving' : isSubscribed ? 'On' : 'Off'}</em>
    </button>
  );
}

export function PhoneMorePage({
  user, location, hrefFor, sections, inPerson, showMessages, unreadMessages,
  onClose, onTour, onSignOut, signingOut,
}: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const displayName = (user?.fullName ?? user?.name ?? user?.email ?? 'Your profile').trim();
  const daily: Row[] = [
    { href: '/client/journal', label: 'Journal', icon: PenLine, tone: 'bronze' },
    { href: '/client/week', label: 'Your week', icon: CalendarDays },
    { href: '/client/schedule', label: inPerson ? 'Studio schedule' : 'Schedule', icon: Clock },
    { href: '/client/progress', label: 'Progress and body', icon: BarChart3 },
    { href: '/client/check-in', label: 'Weekly review', icon: ClipboardList },
  ];
  const community: Row[] = [
    { href: '/client/community', label: 'Community', icon: Users },
    { href: '/client/community?view=groups', label: 'Groups', icon: Shield },
    { href: '/client/challenges', label: 'Challenges', icon: Trophy, tone: 'bronze' },
    ...(showMessages ? [{ href: '/client/comms', label: 'Messages', icon: MessageSquare, badge: unreadMessages }] : []),
  ];
  const library: Row[] = [
    { href: '/client/library/vault', label: 'Vault', icon: BookMarked, tone: 'bronze' },
    { href: '/client/bible', label: 'Bible reader', icon: BookOpen, tone: 'bronze' },
  ];

  return (
    <section className="iphone-more" aria-label="More app sections" data-testid="iphone-more">
      <div className="iphone-more__scroll">
        <h1 className="sr-only">More</h1>
        {user ? (
          <Link href={hrefFor(`/client/members/${user.id}`)} onClick={onClose} className="iphone-more__me">
            {user.avatarUrl
              ? <img src={user.avatarUrl} alt="" />
              : <span className="iphone-more__avatar" aria-hidden="true">{displayName.slice(0, 1).toUpperCase()}</span>}
            <span className="iphone-more__me-copy"><b>{displayName}</b><span>Profile, settings, membership</span></span>
            <ChevronRight className="size-[18px]" aria-hidden="true" />
          </Link>
        ) : null}

        <Group title="Daily">{daily.map(row => <RowLink key={row.href} row={row} hrefFor={hrefFor} onClose={onClose} />)}</Group>
        <Group title="Community">{community.map(row => <RowLink key={row.href} row={row} hrefFor={hrefFor} onClose={onClose} />)}</Group>
        <Group title="Library">{library.map(row => <RowLink key={row.href} row={row} hrefFor={hrefFor} onClose={onClose} />)}</Group>

        <div className="iphone-more__group">
          <p className="iphone-more__grp" id="iphone-more-everything">Everything</p>
          <div className="iphone-more__list" role="group" aria-labelledby="iphone-more-everything">
            {sections.map((section) => {
              const open = expanded === section.label;
              const panelId = `iphone-more-section-${section.label.toLowerCase()}`;
              const SectionIcon = section.icon ?? LayoutGrid;
              return (
                <div key={section.label} className="iphone-more__section">
                  <button type="button" className="iphone-more__row" aria-expanded={open} aria-controls={panelId}
                    onClick={() => setExpanded(open ? null : section.label)}>
                    <span className="iphone-more__ic"><SectionIcon className="size-[18px]" strokeWidth={1.8} aria-hidden="true" /></span>
                    <span className="iphone-more__text">
                      {section.label}
                      {section.description ? <small>{section.description}</small> : null}
                    </span>
                    <ChevronDown className="iphone-more__chev size-[18px]" data-open={open ? 'true' : undefined} aria-hidden="true" />
                  </button>
                  {open ? (
                    <div className="iphone-more__sub" id={panelId}>
                      {section.items.map(({ path, label, desc, icon: Icon }) => (
                        <Link key={`${section.label}-${path}-${label}`} href={hrefFor(path)} onClick={onClose} className="iphone-more__subrow"
                          aria-current={location.startsWith(path) ? 'page' : undefined}>
                          <Icon className="size-[18px]" strokeWidth={1.8} aria-hidden="true" />
                          <span><strong>{label}</strong>{desc ? <small>{desc}</small> : null}</span>
                          <ChevronRight className="size-4" aria-hidden="true" />
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div className="iphone-more__group">
          <p className="iphone-more__grp" id="iphone-more-settings">Settings</p>
          <div className="iphone-more__list" role="group" aria-labelledby="iphone-more-settings">
            {user ? (
              <Link href={hrefFor('/client/account')} onClick={onClose} className="iphone-more__row">
                <span className="iphone-more__ic"><UserCircle className="size-[18px]" strokeWidth={1.8} aria-hidden="true" /></span>
                <span className="iphone-more__text">Account and preferences</span>
              </Link>
            ) : null}
            <PushRow />
            <button type="button" className="iphone-more__row" onClick={onTour}>
              <span className="iphone-more__ic"><Sparkles className="size-[18px]" strokeWidth={1.8} aria-hidden="true" /></span>
              <span className="iphone-more__text">Show me around</span>
            </button>
          </div>
        </div>

        <button type="button" className="iphone-more__signout" onClick={onSignOut} disabled={signingOut}>
          <LogOut className="size-4" aria-hidden="true" />
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </section>
  );
}
